import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { positionsService } from '../services/positions';
import { companiesService } from '@shared/services/companiesService';
import { COMMON_BUTTONS } from '@config/actionButtonConfigs';
import ActionButtons from '@shared/components/ActionButtons';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import {
    extractErrorMessage,
    shouldBlockFetchError,
    showBlockingError,
} from '@shared/services/blockingError';
import useOpenFiles from '@shared/hooks/useOpenFiles';
import { getFileIcon } from '@utils/fileIcons';
import Table from '@shared/components/Table';
import PositionsForm from './PositionsForm';
import DeleteModal from '@shared/components/DeleteModal';
import PositionTranslations from './PositionTranslations';
import PositionRights from './PositionRights';

import { useStatusBadge } from '@shared/components/StatusBadge';
import { useReportGenerator } from '@shared/hooks/useReportGenerator';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

const POSITIONS_FETCH_OPTIONS = { blockingError: true };

function handlePositionsAccessError(error) {
    if (shouldBlockFetchError(error, POSITIONS_FETCH_OPTIONS)) {
        showBlockingError(extractErrorMessage(error));
        return true;
    }

    return false;
}

export default function Positions() {
    const { t } = useTranslation();
    const adminBranchId = useSelector((state) => state.auth.adminBranchId);
    const handleOpenJobDescriptionFile = useOpenFiles('/hr-job-description/file');
    const [selectedData, setSelectedData] = useState(null);
    const [sortBy, setSortBy] = useState('id');
    const [sortDir, setSortDir] = useState('asc');
    const [searchQuery, setSearchQuery] = useState({ code: '', title: '' });
    const [selectedCompany, setSelectedCompany] = useState('');
    const [modalState, setModalState] = useState({ visible: false, type: null });
    const [positionData, setPositionData] = useState(null);
    const [positions, setPositions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [error, setError] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [companiesLoading, setCompaniesLoading] = useState(true);
    const [pageNumber, setpageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    // Initialize react-hook-form
    const formProps = useForm({
        defaultValues: {
            company: '',
            title: '',
            NOC: '',
            fromDate: '',
            toDate: '',
            managedBy: '',
            translated: '',
            description: '',
            indefinite: false
        }
    });

    const { statusBadge } = useStatusBadge();

    // Use the global report generator hook
    const { generatePdfReport, generateExcelReport, pdfLoading, excelLoading } = useReportGenerator({
        title: t('organizationalStructure.positions.title'),
        columns: [
            { key: 'jobCode', label: t('organizationalStructure.positions.table.headers.code') },
            { key: 'longDescription', label: t('organizationalStructure.positions.table.headers.positionTitle') },
            { key: 'nkpd', label: t('organizationalStructure.positions.table.headers.NOC') },
            { key: "workTime", label: t('organizationalStructure.positions.table.headers.workTime') },
            { key: "minSalary", label: t('organizationalStructure.positions.form.minSalary') },
            { key: "maxSalary", label: t('organizationalStructure.positions.form.maxSalary') },
            { key: "addPayedLeave", label: t('organizationalStructure.positions.form.addPayedLeave') },
            { key: "degreeLevel", label: t('organizationalStructure.positions.form.degreeLevel') },
            { key: "workCategory", label: t('organizationalStructure.positions.form.hrLaborCategoryId') },
            { key: "minProfServiceYears", label: t('organizationalStructure.positions.form.minProfServiceYears') },
            { key: "minProfServiceMonths", label: t('organizationalStructure.positions.form.minProfServiceMonths') },
            { 
                key: "leaderPosition", 
                label: t('organizationalStructure.positions.form.isLeaderPosition'),
                render: (item) => item.leaderPosition ? t('common.yes') : t('common.no')
            },
            {key: "categoryOfStaff", label: t('organizationalStructure.positions.form.categoryOfStaff') },
            { key: 'openDate', label: t('organizationalStructure.positions.table.headers.fromDate') },
            {
                key: 'closeDate',
                label: t('organizationalStructure.positions.table.headers.toDate'),
                render: (item) => {
                    if (item.closeDate === '31.12.2099') {
                        return t('organizationalStructure.structuralGroups.form.indefinite');
                    }
                    return item.closeDate || '-';
                }
            },
        ],
        fileName: 'positions_report',
        companyName: '',
        reportService: async () => {
            if (!selectedCompany) {
                throw new Error(t('common.selectRecord'));
            }
            const data = await positionsService.getPositionsReportData(selectedCompany);
            return data;
        },
        customStyles: {
            tableFontSize: '7px'
        }
    });

    // Fetch companies
    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                setCompaniesLoading(true);
                const response = await companiesService.getAllCompanies();
                // Filter companies based on adminBranchId if available
                let filteredCompanies = response;
                if (adminBranchId && response && response.length > 0) {
                    filteredCompanies = response.filter(company => company.id === adminBranchId);
                }
                setCompanies(filteredCompanies);
                // Set default selected company to the first company in the list
                if (filteredCompanies && filteredCompanies.length > 0) {
                    setSelectedCompany(filteredCompanies[0].id);
                }
            } catch (err) {
                console.error('Error fetching companies:', err);
                if (!handlePositionsAccessError(err)) {
                    setError('Failed to load companies');
                } else {
                    setError(null);
                }
            } finally {
                setCompaniesLoading(false);
            }
        };

        if (adminBranchId !== null) {
            fetchCompanies();
        }
    }, [adminBranchId]);

    // Fetch positions when selectedCompany changes
    useEffect(() => {
        fetchPositions();
    }, [selectedCompany]);

    const fetchPositions = async () => {
        if (!selectedCompany) {
            setPositions([]);
            setTotalElements(0);
            setTotalPages(0);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const searchParams = {};
            if (searchQuery.code) {
                searchParams.jobCode = searchQuery.code;
            }
            if (searchQuery.title) {
                searchParams.longDescription = searchQuery.title;
            }

            const data = await positionsService.getAllPositions({
                pageNumber,
                pageSize,
                sortBy,
                sortDir,
                ...searchParams
            });
            setPositions(data.content || []);
            setTotalElements(data.totalElements || 0);
            setTotalPages(data.totalPages || 0);
        } catch (err) {
            console.error('Error fetching positions:', err);
            setPositions([]);
            setTotalElements(0);
            setTotalPages(0);
            if (!handlePositionsAccessError(err)) {
                setError('Failed to fetch positions');
                notifyError(t('common.errorMessage'));
            } else {
                setError(null);
            }
        } finally {
            setLoading(false);
        }
    };

    // Add useEffect to refetch when pagination or sorting changes
    useEffect(() => {
        fetchPositions();
    }, [selectedCompany, pageNumber, pageSize, sortBy, sortDir]);

    const handleSearchClick = () => {
        fetchPositions();
    };

    const searchOptions = [
        { key: 'code', label: t('code') },
        { key: 'title', label: t('title') },
    ];


    const columns = [
        { key: 'jobCode', label: t('organizationalStructure.positions.table.headers.code'), sortable: true },
        { key: 'longDescription', label: t('organizationalStructure.positions.table.headers.title'), sortable: true },
        { key: 'nkpd', label: t('organizationalStructure.positions.table.headers.NOC'), sortable: true },
        { key: 'openDate', label: t('organizationalStructure.positions.table.headers.fromDate'), sortable: true },
        { key: 'closeDate', label: t('organizationalStructure.positions.table.headers.toDate'), sortable: true },
        { key: 'leaderPosition', label: t('organizationalStructure.positions.table.headers.managedBy'), sortable: true },
        {
            key: 'translated', label: t('organizationalStructure.positions.table.headers.transliteration'), sortable: true
        },
        {
            key: 'fileName',
            label: t('organizationalStructure.positions.table.headers.description'),
            sortable: true
        },

    ];

    const actionButtons = (
        <ActionButtons
            buttons={[
                {
                    ...COMMON_BUTTONS.ADD,
                    onClick: () => handleOpenModal('add')
                },
                {
                    ...COMMON_BUTTONS.EDIT,
                    onClick: () => handleOpenModal('edit')
                },
                {
                    ...COMMON_BUTTONS.DELETE,
                    onClick: () => handleOpenModal('delete')
                },
                {
                    ...COMMON_BUTTONS.TRANSLATE,
                    onClick: () => handleOpenModal('translations')
                },
                {
                    ...COMMON_BUTTONS.RIGHTS,
                    onClick: () => handleOpenModal('rights')
                },
                {
                    ...COMMON_BUTTONS.DOWNLOAD_PDF,
                    onClick: generatePdfReport,
                    disabled: pdfLoading || !selectedCompany,
                    icon: pdfLoading ? faSpinner : COMMON_BUTTONS.DOWNLOAD_PDF.icon,
                    iconProps: pdfLoading ? { spin: true } : {},
                    title: pdfLoading ? "Генериране на PDF..." : "common.downloadPDF",
                    showWhen: 'always'
                },
                {
                    ...COMMON_BUTTONS.EXCEL,
                    onClick: generateExcelReport,
                    disabled: excelLoading || !selectedCompany,
                    icon: excelLoading ? faSpinner : COMMON_BUTTONS.EXCEL.icon,
                    iconProps: excelLoading ? { spin: true } : {},
                    title: excelLoading ? "Генериране на Excel..." : "common.downloadExcel",
                    showWhen: 'always'
                }
            ]}
            selectedRecord={selectedData}
        />
    );



    const handleCheckboxChange = (index) => {
        const position = positions[index];
        setSelectedData(selectedData === position.id ? null : position.id);
    };

    const handleSort = (key) => {
        if (sortBy === key) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(key);
            setSortDir('asc');
        }
    };

    const handleOpenModal = async (type) => {
        if (type === 'edit' && selectedData) {
            try {
                setModalLoading(true);
                const fullPositionData = await positionsService.getJobRightsById(selectedData);
                setPositionData(fullPositionData);
            } catch (error) {
                console.error('Error fetching position details:', error);
                notifyError(t('common.errorMessage'));
                return;
            } finally {
                setModalLoading(false);
            }
        } else if (type === 'translations' && selectedData) {
            const position = positions.find(p => p.id === selectedData);
            setPositionData(position);
        } else if (type === 'rights' && selectedData) {
            const position = positions.find(p => p.id === selectedData);
            setPositionData(position);
        } else {
            setPositionData(null);
        }
        setModalState({ visible: true, type });
    };

    const handleCloseModal = () => {
        setModalState({ visible: false, type: null });
        setPositionData(null);
        formProps.reset(); // Reset form values
    };

    // Reset form when modal type changes
    useEffect(() => {
        if (modalState.type === 'add') {
            formProps.reset(); // Reset form for new entry
        }
    }, [modalState.type, formProps]);

    const handleConfirm = async (formData) => {
        try {
            if (modalState.type === 'edit') {
                await positionsService.updatePosition(selectedData, formData);
                notifySuccess(t('common.updatedSuccessfully'));
            } else if (modalState.type === 'delete') {
                await positionsService.deletePosition(selectedData);
                notifySuccess(t('common.deletedSuccessfully'));
                setSelectedData(null);
            } else {
                // Create new position
                await positionsService.createPosition(formData);
                notifySuccess(t('common.createdSuccessfully'));
            }
            // Close modal first
            handleCloseModal();
            // Then refresh positions
            await fetchPositions();
        } catch (error) {
            const errorMessage = error.response?.data?.message;
            notifyError(errorMessage);
            console.error('Error saving position:', error);

        }
    };



    return (
        <>
            <Table
                // Data and state
                data={positions}
                loading={loading}
                error={error}
                selectedData={selectedData}
                sortBy={sortBy}
                sortDir={sortDir}
                totalElements={totalElements}
                totalPages={totalPages}
                searchQuery={searchQuery}
                searchOptions={searchOptions}
                showCompanySelect={true}
                companyOptions={companies}
                selectedCompany={selectedCompany}
                setSelectedCompany={setSelectedCompany}
                pageNumber={pageNumber}
                pageSize={pageSize}
                setpageNumber={setpageNumber}
                setPageSize={setPageSize}
                getData={fetchPositions}

                // Column configuration
                columns={columns}

                // Title and actions
                title={t('organizationalStructure.positions.title')}
                actionButtons={actionButtons}

                // Handlers
                showPageSizeSelector={false}
                showPagination={true}
                handleSort={handleSort}
                handleCheckboxChange={handleCheckboxChange}
                handleSearchClick={handleSearchClick}
                setSearchQuery={setSearchQuery}

                // Custom renderers
                renderCell={(item, column) => {
                    // Handle special render cases first
                    if (column.key === 'closeDate') {
                        if (item.closeDate === '31.12.2099') {
                            return t('organizationalStructure.structuralGroups.form.indefinite');
                        }
                        return item.closeDate;
                    }
                    if (column.key === 'fileName') {
                        if (!item.fileName) return '';
                        return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <img
                                    src={getFileIcon(item.fileName)}
                                    alt="View file"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenJobDescriptionFile(item.jobDescriptionId);
                                    }}
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                        cursor: 'pointer',
                                        transition: 'opacity 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                    title={`Click to view: ${item.fileName}`}
                                />
                            </div>
                        );
                    }
                    // Use statusBadge for other columns
                    return statusBadge(item, column);
                }}
            />

            {modalState.visible && (modalState.type === 'add' || modalState.type === 'edit') && (
                <PositionsForm
                    onClose={handleCloseModal}
                    onConfirm={handleConfirm}
                    editData={positionData}
                    formProps={formProps}
                    companies={companies}
                    companiesLoading={companiesLoading}
                    selectedCompany={selectedCompany}
                />
            )}
            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirm}
                    selectedData={selectedData}
                    name={positions.find(p => p.id === selectedData)?.title}
                    entityType="record"
                />
            )}

            {modalState.visible && modalState.type === 'translations' && (
                <PositionTranslations
                    onClose={handleCloseModal}
                    position={positionData}
                    onSuccess={() => {
                        fetchPositions();
                    }}
                />
            )}

            {modalState.visible && modalState.type === 'rights' && (
                <PositionRights
                    onClose={handleCloseModal}
                    positionId={selectedData}
                    position={positionData}
                />
            )}
        </>
    );
}


